import { stampInstant } from './portalTime';
import { PortalHourlyRow, PortalJob, PUNCH_TYPE_BUTTON, PunchSwitchKind, TYPE_REGULAR } from './portalTypes';

/**
 * Employee Time Portal — request bodies for the punch writes, field-for-field what mobile
 * sends (MobileApp src/api/timeEntryHourlyAPI.ts jsonBodyForPost / jsonBodyForPut), with
 * the tap instant stamped in the TENANT zone. Pure.
 *
 *   Clock In / Clock Back In  POST /portal/timeEntryHourly         (+ clientPunchID)
 *   Clock Out                 PUT  /portal/timeEntryHourly/:id     (the WHOLE row — PUT replaces it)
 *   Meal start / end, switch  POST /portal/timeEntryHourly/:id/switch (one transaction; + clientPunchID)
 */

export function buildClockInBody(employeeID: number, tapMs: number, zone: string, job: PortalJob): Record<string, unknown> {
  const at = stampInstant(tapMs, zone);
  return {
    employeeID,
    inDateTime: at.iso,
    inDateTimeOffset: at.offset,
    outDateTime: null,
    outDateTimeOffset: null,
    type: TYPE_REGULAR,
    punchTypeIn: PUNCH_TYPE_BUTTON,
    punchTypeOut: null,
    projectID: job.projectID ?? null,
    accountCodeID: job.accountCodeID ?? null,
    projectPhaseID: job.projectPhaseID ?? null,
    serviceOrderText: null,
    customerServiceWarrantyID: null,
    locationName: null,
    description: null,
    serviceOrderID: null,
    serviceOrderScopeID: null,
    tempServiceOrderDescription: null,
  };
}

/**
 * Clock Out = the open row with its out side set. Every field is resent from the row as
 * the server holds it, because PUT replaces the row (an omitted job would be cleared).
 */
export function buildClockOutBody(row: PortalHourlyRow, tapMs: number, zone: string): Record<string, unknown> {
  const out = stampInstant(tapMs, zone);
  const body: Record<string, unknown> = {
    inDateTime: new Date(Date.parse(row.inDateTime)).toISOString(),
    inDateTimeOffset: row.inDateTimeOffset,
    outDateTime: out.iso,
    outDateTimeOffset: out.offset,
    type: row.type,
    punchTypeIn: row.punchTypeIn,
    punchTypeOut: PUNCH_TYPE_BUTTON,
    projectID: row.project?.projectID ?? null,
    accountCodeID: row.account?.accountCodeID ?? null,
    projectPhaseID: row.projectPhase?.projectPhaseID ?? null,
    serviceOrderText: row.serviceOrderText || null,
    customerServiceWarrantyID: row.customerServiceWarranty?.customerServiceWarrantyID || null,
    locationName: row.locationName || null,
    description: row.description || null,
    serviceOrderID: row.serviceOrder?.serviceOrderID ?? null,
    serviceOrderScopeID: row.serviceOrderScopeID ?? null,
    tempServiceOrderDescription: row.tempServiceOrderDescription ?? null,
  };
  if (row.inLatitude != null && row.inLongitude != null) {
    body.inLatitude = row.inLatitude;
    body.inLongitude = row.inLongitude;
  }
  return body;
}

/**
 * The atomic switch. `closeAt` is the tap instant (the server derives the offset from the
 * employee's zone). Only a job switch names the next job; meals copy it server-side.
 */
export function buildSwitchBody(kind: PunchSwitchKind, tapMs: number, job: PortalJob | null): Record<string, unknown> {
  const next: Record<string, unknown> = { type: kind };
  if (kind === 'jobSwitch') {
    next.projectID = job?.projectID ?? null;
    next.projectPhaseID = job?.projectPhaseID ?? null;
    next.accountCodeID = job?.accountCodeID ?? null;
  }
  return { closeAt: new Date(tapMs).toISOString(), next };
}

/** A switch needs a project or a cost code (the API's INVALID_SWITCH rule). */
export function isSwitchableJob(job: PortalJob | null | undefined): boolean {
  return !!job && (job.projectID != null || job.accountCodeID != null);
}
